import { MouseEventHandler } from "react";
import { cn } from "../../../utils/classes";
import styles from "./version-stamp.module.css";

export type VersionStampProps = {
  /** The app's current version string, e.g. `"0.4.1"` — no `v` prefix, the
   * caller decides that (this component renders exactly what it's given). */
  version: string;
  /** Mirrors the owning `<NavRail>`'s expanded state — collapsed shows just
   * the version; expanded adds "· Changelog". JS-gated rather than a CSS
   * ancestor selector, same reasoning as `<ThemeToggle>`'s own `expanded`
   * prop: cross-file CSS Modules can't reliably target an ancestor class
   * defined in a different module. */
  expanded?: boolean;
  /** True when the current route is the changelog destination. */
  active?: boolean;
  href?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  className?: string;
};

/**
 * Bottom-of-rail version stamp, sitting below `<ThemeToggle>`/
 * `<IdentityControl>` in the footer. A real `<a href>` (not a `<button>`) so
 * cmd/ctrl/middle-click all work — same reasoning as every other rail nav
 * item (`<NavRailItem>`). Collapsed: "0.4.1". Expanded: "0.4.1 · Changelog".
 */
export function VersionStamp({
  version,
  expanded = false,
  active = false,
  href = "/changelog",
  onClick,
  className,
}: VersionStampProps) {
  return (
    <a
      href={href}
      onClick={onClick}
      className={cn(
        styles.stamp,
        expanded && styles.expanded,
        active && styles.active,
        className,
      )}
      aria-label={
        expanded ? `Version ${version} — view changelog` : `Version ${version}`
      }
      title={expanded ? undefined : `Version ${version} — view changelog`}
    >
      <span className={styles.versionBox}>
        <span className={styles.version}>{version}</span>
      </span>
      {expanded && (
        <>
          <span className={styles.separator} aria-hidden="true">
            ·
          </span>
          <span className={styles.changelogLabel}>Changelog</span>
        </>
      )}
    </a>
  );
}
